// Suspect-match reason categorisation. The backend (match.service.ts §6.5) emits free-form reason clauses joined
// by '; '. Map each clause to a category + colour + severity for the UI.
export type SuspectSeverity = 'high' | 'medium' | 'low';

const SEVERITY_RANK: Record<SuspectSeverity, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

export const SEVERITY_COLOR: Record<SuspectSeverity, string> = {
  high: 'red',
  medium: 'orange',
  low: 'default',
};

export const categoriseSuspectReason = (
  clause: string
): { label: string; color: string; severity: SuspectSeverity } => {
  const c = clause.toLowerCase();
  const opp = c.includes('opponent');
  if (c.includes('exceeds theoretical max'))
    return {
      label: opp ? 'Opp runs > max' : 'Runs > max',
      color: 'red',
      severity: 'high',
    };
  if (c.includes('exceeds match.overs'))
    return { label: opp ? 'Opp overs' : 'Overs', color: 'volcano', severity: 'high' };
  // "card <id> credited with N wickets in N legal balls" (wicket ball check, 26 Oct 2026).
  if (c.includes('legal balls'))
    return { label: 'Wickets > balls', color: 'magenta', severity: 'high' };
  if (c.includes('out of range'))
    return { label: opp ? 'Opp wickets' : 'Wickets', color: 'magenta', severity: 'high' };
  if (c.includes('shot breakdown'))
    return {
      label: opp ? 'Opp breakdown' : 'Breakdown',
      color: 'orange',
      severity: 'medium',
    };
  if (c.includes('duration')) return { label: 'Duration', color: 'gold', severity: 'medium' };
  return { label: 'Other', color: 'default', severity: 'low' };
};

export const splitReasonClauses = (reason: string): string[] =>
  reason
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean);

export const worstSuspectSeverity = (reason: string): SuspectSeverity => {
  let worst: SuspectSeverity = 'low';
  for (const clause of splitReasonClauses(reason)) {
    const sev = categoriseSuspectReason(clause).severity;
    if (SEVERITY_RANK[sev] > SEVERITY_RANK[worst]) worst = sev;
  }
  return worst;
};
